import React, { useCallback, useContext, useEffect, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import useDocumentPreview from '../../../../components/Documents/useDocumentPreview'
import FuelLogForm, { TANK_LEVELS } from '../../fuel/FuelLogForm'
import { VehicleContext } from '../SingleVehicle'
import { SectionHeader, StatTile } from './SectionKit'
import FuelCards from './FuelCards'

const FLEET_TEAMS = ['Fleet', 'Make Ready']
const OWN_DELETE_WINDOW_HOURS = 24

const money = (value) =>
  value === undefined || value === null ? '—' : `£${Number(value).toFixed(2)}`

const tankLabel = (value) =>
  TANK_LEVELS.find((level) => level.value === value)?.label ?? '—'

// Refuels logged against this vehicle (crew record them from Record Fuel on
// web/mobile, or here). Delete rules mirror server/routers/fleetEquipment/fuelLogs.js.
const FuelSection = () => {
  const { vehicle } = useContext(VehicleContext)
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const { openPreview, previewModal } = useDocumentPreview()
  const [data, setData] = useState({ logs: [], last30Days: null })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const isFleetManager =
    !!user?.security?.isSiteAdmin ||
    (user?.employee?.teams ?? []).some((t) => FLEET_TEAMS.includes(t))
  const canDelete = (log) =>
    isFleetManager ||
    (String(log.recordedBy?._id ?? log.recordedBy) === String(user?._id) &&
      moment().diff(moment(log.createdAt), 'hours') < OWN_DELETE_WINDOW_HOURS)

  const load = useCallback(() => {
    if (!vehicle?._id) return
    setLoading(true)
    setError(false)
    API.get(`/vehicle/${vehicle._id}/fuel-logs`)
      .then((res) => {
        if (res.status === 200) {
          setData({
            logs: res.data?.logs ?? [],
            last30Days: res.data?.last30Days ?? null,
          })
        } else {
          setError(true)
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [vehicle?._id])

  useEffect(() => {
    load()
  }, [load])

  const handleDelete = async () => {
    if (!confirmDelete) return
    setDeleting(true)
    const res = await API.delete(`/fuel-logs/${confirmDelete._id}`)
    setDeleting(false)
    if (res.status === 200) {
      setConfirmDelete(null)
      setToast?.({ message: 'Fuel record deleted' })
      load()
    } else {
      setToast?.({
        message: 'Unable to delete that fuel record',
        severity: 'error',
      })
    }
  }

  const lastFill = data.logs[0]
  const summary = data.last30Days

  return (
    <Stack spacing={2}>
      <SectionHeader
        title="Fuel"
        caption="Fuel cards kept with the vehicle, and refuels recorded by crew"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setFormOpen(true)}
          >
            Record fuel
          </Button>
        }
      />

      <FuelCards canEdit={isFleetManager} />

      <Typography variant="subtitle1" fontWeight={700} sx={{ pt: 1 }}>
        Refuels
      </Typography>

      {loading && <LinearProgress />}
      {error && (
        <Alert severity="error">
          Unable to load fuel records for this vehicle. Please try again later.
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Box>
            <Grid container spacing={1.5}>
              <Grid item xs={6} md={3} display="flex">
                <StatTile
                  label="Last refuel"
                  value={
                    lastFill
                      ? moment(lastFill.filledAt).format('D MMM YYYY')
                      : null
                  }
                  caption={
                    lastFill
                      ? `Tank ${tankLabel(lastFill.tankLevel)}`
                      : 'None recorded'
                  }
                />
              </Grid>
              <Grid item xs={6} md={3} display="flex">
                <StatTile
                  label="Fills (30 days)"
                  value={String(summary?.fills ?? 0)}
                />
              </Grid>
              <Grid item xs={6} md={3} display="flex">
                <StatTile
                  label="Litres (30 days)"
                  value={`${summary?.litres ?? 0} L`}
                />
              </Grid>
              <Grid item xs={6} md={3} display="flex">
                <StatTile
                  label="Spend (30 days)"
                  value={money(summary?.cost ?? 0)}
                  caption="Where a cost was recorded"
                />
              </Grid>
            </Grid>
          </Box>
          {data.logs.length ? (
            <TableContainer
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Litres</TableCell>
                    <TableCell align="right">£/litre</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Tank after</TableCell>
                    <TableCell align="right">Mileage</TableCell>
                    <TableCell>Recorded by</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.logs.map((log) => (
                    <TableRow key={log._id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {moment(log.filledAt).format('D MMM YYYY HH:mm')}
                        {log.notes && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="div"
                            sx={{ whiteSpace: 'normal', maxWidth: 260 }}
                          >
                            {log.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{log.litres}</TableCell>
                      <TableCell align="right">
                        {log.pricePerLitre !== undefined &&
                        log.pricePerLitre !== null
                          ? `£${Number(log.pricePerLitre).toFixed(3)}`
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {money(log.totalCost)}
                      </TableCell>
                      <TableCell>{tankLabel(log.tankLevel)}</TableCell>
                      <TableCell align="right">
                        {log.mileage !== undefined && log.mileage !== null
                          ? Number(log.mileage).toLocaleString('en-GB')
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {[log.recordedBy?.firstName, log.recordedBy?.lastName]
                          .filter(Boolean)
                          .join(' ') || '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {log.receipt?.key && (
                          <Tooltip title="View receipt">
                            <IconButton
                              size="small"
                              onClick={() =>
                                openPreview({
                                  key: log.receipt.key,
                                  name: log.receipt.name,
                                })
                              }
                            >
                              <ReceiptLongOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete(log) && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setConfirmDelete(log)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.disabled">
              No fuel has been recorded for this vehicle yet.
            </Typography>
          )}
        </>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Record fuel — {vehicle?.callSign}
          <IconButton onClick={() => setFormOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {formOpen && (
            <FuelLogForm
              vehicle={vehicle}
              onCancel={() => setFormOpen(false)}
              onSaved={() => {
                setFormOpen(false)
                load()
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!confirmDelete}
        onClose={() => !deleting && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete fuel record?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmDelete &&
              `${confirmDelete.litres} litres on ${moment(confirmDelete.filledAt).format('D MMM YYYY HH:mm')}`}
            {confirmDelete?.receipt?.key &&
              ' — the receipt will be deleted too.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setConfirmDelete(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {previewModal}
    </Stack>
  )
}

export default FuelSection
